/**
 * État formulaire « nouvelle consultation » partagé entre PatientDetail et QuickDiagnostic.
 * Complément API (analyses, résultats, ordonnances) — aligné sur HELPEUR / POST consultations.
 */

export const CONSULTATION_STEPS = [
  { id: "ouverture", label: "Observation & Analyses" },
  { id: "cloture", label: "Résultats & Ordonnances" },
] as const;

export const CONSULTATION_EXIT_STATES = [
  "decede",
  "gueri",
  "en_traitement",
  "sorti_sans_avis_medical",
] as const;

export type ConsultationExitState = (typeof CONSULTATION_EXIT_STATES)[number];

export type NewConsultationFormState = {
  observations: string;
  analyses: string[];
  analysisInput: string;
  disease: string;
  resultNote: string;
  ordonnances: { medicament: string; note: string; renouvelable: boolean }[];
};

export const COMPLEMENT_LIMITS = {
  observationMax: 8000,
  analysisItemMax: 200,
  maxAnalyses: 50,
  diseaseMax: 200,
  resultNoteMax: 4000,
  medicamentMax: 200,
  ordonnanceNoteMax: 500,
  maxOrdonnanceRows: 20,
} as const;

function clamp(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max);
}

export function createEmptyNewConsultationForm(): NewConsultationFormState {
  return {
    observations: "",
    analyses: [],
    analysisInput: "",
    disease: "Maladie",
    resultNote: "",
    ordonnances: [{ medicament: "", note: "", renouvelable: false }],
  };
}

/**
 * Construit l’objet `complement` pour le body POST /patient/consultations (hors observations, déjà en racine).
 */
export function buildConsultationComplement(state: NewConsultationFormState): Record<string, unknown> | undefined {
  const analyses = state.analyses
    .map((a) => clamp(a.trim(), COMPLEMENT_LIMITS.analysisItemMax))
    .filter(Boolean)
    .slice(0, COMPLEMENT_LIMITS.maxAnalyses);

  const ordonnances = state.ordonnances
    .filter((o) => o.medicament.trim())
    .slice(0, COMPLEMENT_LIMITS.maxOrdonnanceRows)
    .map((o) => ({
      medicament: clamp(o.medicament.trim(), COMPLEMENT_LIMITS.medicamentMax),
      note: clamp(o.note.trim(), COMPLEMENT_LIMITS.ordonnanceNoteMax),
      renouvelable: o.renouvelable,
    }));

  const disease =
    state.disease !== "Maladie" ? clamp(state.disease, COMPLEMENT_LIMITS.diseaseMax) : "";
  const resultNote = clamp(state.resultNote.trim(), COMPLEMENT_LIMITS.resultNoteMax);

  const resultats =
    disease || resultNote
      ? {
          disease: disease || "Non précisé",
          note: resultNote,
        }
      : undefined;

  const complement: Record<string, unknown> = {};
  if (analyses.length) complement.analyses = analyses;
  if (resultats) complement.resultats = resultats;
  if (ordonnances.length) complement.ordonnances = ordonnances;

  return Object.keys(complement).length > 0 ? complement : undefined;
}

export function truncateObservations(text: string): string {
  return clamp(text.trim(), COMPLEMENT_LIMITS.observationMax);
}

/** Motif affiché / envoyé (aligné sur l’existant PatientDetail). */
export function consultationMotifFromForm(state: NewConsultationFormState): string {
  return state.disease !== "Maladie" ? state.disease : "Consultation";
}

export function extractConsultationEntityId(data: unknown): string | undefined {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if (typeof o.id === "string" && o.id) return o.id;
    const inner = o.data;
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      const id = (inner as Record<string, unknown>).id;
      if (typeof id === "string" && id) return id;
    }
  }
  return undefined;
}
